import {
  $applyNodeReplacement,
  DecoratorNode,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from "lexical";

import { sanitizeHtml } from "../../utils/sanitizeHtml";

export type SerializedRawHtmlNode = Spread<
  { html: string; type: "raw-html"; version: 1 },
  SerializedLexicalNode
>;

export class RawHtmlNode extends DecoratorNode<null> {
  __html: string;

  static getType(): string {
    return "raw-html";
  }

  static clone(node: RawHtmlNode): RawHtmlNode {
    return new RawHtmlNode(node.__html, node.__key);
  }

  constructor(html: string, key?: NodeKey) {
    super(key);
    this.__html = sanitizeHtml(html);
  }

  createDOM(): HTMLElement {
    const el = document.createElement("div");
    el.innerHTML = this.__html;
    el.contentEditable = "false";
    el.style.userSelect = "none";
    return el;
  }

  updateDOM(): false {
    return false;
  }

  decorate(): null {
    return null;
  }

  exportDOM(): { element: HTMLElement | null } {
    const el = document.createElement("div");
    el.innerHTML = this.__html.trim();
    return { element: el.firstElementChild as HTMLElement | null };
  }

  isInline(): false {
    return false;
  }

  isIsolated(): true {
    return true;
  }

  exportJSON(): SerializedRawHtmlNode {
    return { type: "raw-html", html: this.__html, version: 1 };
  }

  static importJSON(json: SerializedRawHtmlNode): RawHtmlNode {
    return $createRawHtmlNode(json.html);
  }

  getTextContent(): string {
    return "";
  }
}

export function $createRawHtmlNode(html: string): RawHtmlNode {
  return $applyNodeReplacement(new RawHtmlNode(html));
}

export function $isRawHtmlNode(
  node: LexicalNode | null | undefined,
): node is RawHtmlNode {
  return node instanceof RawHtmlNode;
}
